import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { removeLayerFromMap } from '../redux/actions/layersMap';
import { activeGetInfo, passiveGetInfo } from '../redux/actions/info';
import { activeInfoPanel } from '../redux/actions/infoPanel';
import { 
  Box, 
  Typography, 
  IconButton, 
  Slider, 
  Tooltip,
  Paper,
  Stack,
  Chip
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import LayersIcon from '@mui/icons-material/Layers';

import { modernDesignSystem } from '../constants/themes';

const LayerSwitcherElement = (props) => {
  const dispatch = useDispatch();
  let layer = props.layer;

  const [visible, setVisible] = useState(true);

  const { map } = useSelector((state) => state.globalMaps[0]);

  const infoLayer = useSelector((state) => state.infoPanel);
  const [activeLayer, setActiveLayer] = useState(null);

  const layerRemove = () => {
    dispatch(removeLayerFromMap(map, layer));
  };

  const layerSetVisible = () => {
    setVisible(!visible);
    layer.setVisible(!visible);
  };

  const layerZoomToExtend = () => {
    try {
      map.getView().fit(layer.getExtent());
    } catch (err) {
      console.log(err);
      alert('Katmanın sınırları mevcut değil!');
    }
  };

  const layerOpacity = (e) => {
    let opacity = e.target.value;
    layer.setOpacity(opacity / 100);
  };

  const layerInfo = (e) => {
    let view = map.getView();
    dispatch(activeGetInfo(map, view, layer));
    dispatch(activeInfoPanel(layer));
    setActiveLayer(layer);
  };

  useEffect(() => {
    setActiveLayer(infoLayer);
  }, [infoLayer]);

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        maxWidth: '100%',
        backgroundColor: activeLayer === layer 
          ? modernDesignSystem.colors.primary.surface 
          : modernDesignSystem.colors.neutral.gray50, // Hafif gri arka plan
        border: `1px solid ${
          activeLayer === layer 
            ? modernDesignSystem.colors.primary.main 
            : modernDesignSystem.colors.neutral.gray300 // Daha belirgin border
        }`,
        borderRadius: modernDesignSystem.borders.radius.medium,
        margin: '3px 0', // Biraz daha margin
        padding: '8px 10px', // Biraz daha padding
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)', // Hafif gölge
        
        // Hover efekti - daha belirgin
        '&:hover': {
          backgroundColor: activeLayer === layer 
            ? modernDesignSystem.colors.primary.light 
            : modernDesignSystem.colors.neutral.white,
          borderColor: modernDesignSystem.colors.primary.main,
          transform: 'translateY(-2px)', // Daha belirgin yükselme
          boxShadow: '0 4px 12px rgba(46, 125, 50, 0.15)', // Yeşil tonunda gölge
        }
      }}
    >
      {/* Ana Kontrol Satırı */}
      <Stack 
        direction="row" 
        alignItems="center" 
        spacing={0.5}
        sx={{ minHeight: '32px' }} // Sabit minimum yükseklik
      >
        {/* Görünürlük Toggle */}
        <Tooltip title={visible ? "Katmanı Gizle" : "Katmanı Göster"} arrow>
          <IconButton 
            onClick={layerSetVisible}
            size="small"
            sx={{
              padding: '4px',
              color: visible 
                ? modernDesignSystem.colors.primary.main 
                : modernDesignSystem.colors.neutral.gray400,
              '&:hover': {
                backgroundColor: modernDesignSystem.colors.primary.surface,
                color: modernDesignSystem.colors.primary.dark,
              }
            }}
          >
            {visible ? 
              <VisibilityIcon fontSize="small" /> : 
              <VisibilityOffIcon fontSize="small" />
            }
          </IconButton>
        </Tooltip>

        {/* Katman İsmi ve Icon */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          flex: 1, 
          minWidth: 0,
          gap: '4px'
        }}>
          <LayersIcon 
            fontSize="small" 
            sx={{ 
              color: modernDesignSystem.colors.primary.main,
              flexShrink: 0
            }} 
          />
          <Typography
            variant="body2"
            sx={{
              fontSize: '13px',
              fontWeight: 500,
              color: modernDesignSystem.colors.neutral.gray800,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              lineHeight: '1.2'
            }}
          >
            {layer.get('title')}
          </Typography>
        </Box>

        {/* Aksiyon Butonları */}
        <Stack direction="row" spacing={0.25}>
          <Tooltip title="Katman Bilgisi" arrow>
            <IconButton 
              onClick={() => alert('Lejant bilgisi')}
              size="small"
              sx={{
                padding: '3px',
                color: modernDesignSystem.colors.neutral.gray600,
                '&:hover': {
                  backgroundColor: modernDesignSystem.colors.secondary.surface,
                  color: modernDesignSystem.colors.secondary.main,
                }
              }}
            >
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Katmana Yakınlaştır" arrow>
            <IconButton 
              onClick={layerZoomToExtend}
              size="small"
              sx={{
                padding: '3px',
                color: modernDesignSystem.colors.neutral.gray600,
                '&:hover': {
                  backgroundColor: modernDesignSystem.colors.tertiary.surface,
                  color: modernDesignSystem.colors.tertiary.main,
                }
              }}
            >
              <ZoomOutMapIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Katmanı Sil" arrow>
            <IconButton 
              onClick={layerRemove}
              size="small"
              sx={{
                padding: '3px',
                color: modernDesignSystem.colors.neutral.gray600,
                '&:hover': {
                  backgroundColor: modernDesignSystem.colors.error.light,
                  color: modernDesignSystem.colors.error.main,
                }
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Şeffaflık Slider'ı */}
      <Box sx={{ 
        paddingTop: '6px',
        paddingBottom: '0px', // Alt boşluğu kaldır
        paddingLeft: '4px',
        paddingRight: '4px'
      }}>
        <Tooltip title="Katman Şeffaflığı" arrow>
          <Slider
            size="small"
            defaultValue={100}
            min={0}
            max={100}
            onChange={layerOpacity}
            valueLabelDisplay="auto" // Değer gösterimini aç
            valueLabelFormat={(value) => `${value}%`} // Yüzde formatı
            sx={{
              height: '6px', // Biraz daha kalın
              '& .MuiSlider-thumb': {
                width: 14,
                height: 14,
                backgroundColor: modernDesignSystem.colors.primary.main,
                border: `2px solid ${modernDesignSystem.colors.neutral.white}`,
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                '&:hover': {
                  boxShadow: `0 0 0 8px ${modernDesignSystem.colors.primary.main}20`,
                },
                '&.Mui-focusVisible': {
                  boxShadow: `0 0 0 8px ${modernDesignSystem.colors.primary.main}30`,
                }
              },
              '& .MuiSlider-track': {
                backgroundColor: modernDesignSystem.colors.primary.main,
                border: 'none',
                height: '6px',
              },
              '& .MuiSlider-rail': {
                backgroundColor: modernDesignSystem.colors.neutral.gray300,
                height: '6px',
              },
              '& .MuiSlider-valueLabel': {
                backgroundColor: modernDesignSystem.colors.primary.main,
                color: modernDesignSystem.colors.neutral.white,
                fontSize: '11px',
                fontWeight: 600,
                '&::before': {
                  borderTopColor: modernDesignSystem.colors.primary.main,
                }
              }
            }}
          />
        </Tooltip>
      </Box>

      {/* Aktif Layer İndikatörü */}
      {activeLayer === layer && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '3px',
            height: '100%',
            backgroundColor: modernDesignSystem.colors.primary.main,
            borderRadius: '0 2px 2px 0'
          }}
        />
      )}
    </Paper>
  );
};

export default LayerSwitcherElement;
